import { CampaignStatus } from './campaignStatus';
import { normalizeDemandPage } from '../../agency/normalizeDemandPage';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { z } from 'zod';

import { formatDisplayDate, formatUsd } from '../../../lib/format';
import { dashboardT } from '../../../lib/messages';

export { CampaignStatus } from './campaignStatus';

export enum LineItemDeliveryStatus {
  Delivering = 'DELIVERING',
  GoalMet = 'GOAL_MET',
  Completed = 'COMPLETED',
  Ready = 'READY',
}

export enum CampaignSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

const nullableString = z.string().nullable().optional().transform(toOptional);
const nullableNumber = z.number().nullable().optional().transform(toOptional);

export const campaignSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  status: nullableString,
  createdOn: nullableString,
  brandId: nullableString,
  brand: z
    .looseObject({ id: nullableString, name: nullableString })
    .nullable()
    .optional()
    .transform(toOptional),
});

export const campaignsPageSchema = z.preprocess(
  normalizeDemandPage,
  z.looseObject({
    content: z.array(campaignSchema),
    totalElements: z.number(),
    totalPages: z.number().optional(),
    number: z.number().optional(),
    size: z.number().optional(),
  }),
);

export const lineItemSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  status: nullableString,
  deliveryStatus: nullableString,
  startDate: nullableString,
  endDate: nullableString,
  cpm: nullableNumber,
  deliveredImpressions: nullableNumber,
  impressionsGoal: nullableNumber,
  deliveredImpressionsPct: nullableNumber,
});

export const lineItemsPageSchema = z.preprocess(
  normalizeDemandPage,
  z.looseObject({
    content: z.array(lineItemSchema),
    totalElements: z.number(),
    totalPages: z.number().optional(),
    number: z.number().optional(),
    size: z.number().optional(),
  }),
);

export type Campaign = z.infer<typeof campaignSchema>;
export type CampaignsPage = z.infer<typeof campaignsPageSchema>;
export type LineItem = z.infer<typeof lineItemSchema>;
export type LineItemsPage = z.infer<typeof lineItemsPageSchema>;

export type CampaignPayload = {
  name: string;
  brandId: string;
  status: CampaignStatus;
};

export type CampaignsFilter = {
  agencyIds?: string[];
  name: string;
  page: number;
  size: number;
  sortProperty: readonly string[];
  sortDirection: CampaignSortDirection;
  status: CampaignStatus[];
  brandId: string;
};

export type LineItemsFilter = {
  agencyIds?: string[];
  campaignId: string;
  name: string;
  page: number;
  size: number;
  sortProperty: readonly string[];
  sortDirection: CampaignSortDirection;
  status: CampaignStatus[];
  deliveryStatus: LineItemDeliveryStatus[];
};

export const campaignPageSizes = [5, 10, 25, 50, 100] as const;
export const campaignStatusFilterOptions = [
  CampaignStatus.Active,
  CampaignStatus.Archived,
  CampaignStatus.Paused,
] as const;
export const campaignFormStatusOptions = [CampaignStatus.Active, CampaignStatus.Paused] as const;
export const campaignStatusLabels: Record<CampaignStatus, string> = {
  get [CampaignStatus.Active]() {
    return dashboardT('campaigns.status.active');
  },
  get [CampaignStatus.Archived]() {
    return dashboardT('campaigns.status.archived');
  },
  get [CampaignStatus.Paused]() {
    return dashboardT('campaigns.status.paused');
  },
  get [CampaignStatus.Draft]() {
    return dashboardT('campaigns.status.draft');
  },
  get [CampaignStatus.Completed]() {
    return dashboardT('campaigns.status.completed');
  },
};
export const lineItemDeliveryStatusFilterOptions = [
  LineItemDeliveryStatus.Delivering,
  LineItemDeliveryStatus.GoalMet,
  LineItemDeliveryStatus.Completed,
  LineItemDeliveryStatus.Ready,
] as const;

export const defaultCampaignsFilter: CampaignsFilter = {
  name: '',
  page: 0,
  size: 25,
  sortProperty: ['createdOn'],
  sortDirection: CampaignSortDirection.Descending,
  status: [CampaignStatus.Active],
  brandId: '',
};

export const defaultLineItemsFilter: LineItemsFilter = {
  campaignId: '',
  name: '',
  page: 0,
  size: 25,
  sortProperty: ['createdOn'],
  sortDirection: CampaignSortDirection.Descending,
  status: [CampaignStatus.Active],
  deliveryStatus: [],
};

export function serializeCampaignsFilter(filter: CampaignsFilter) {
  const params = new URLSearchParams({
    name: filter.name,
    page: String(filter.page),
    size: String(filter.size),
    sortProperty: filter.sortProperty.join(','),
    sortDirection: filter.sortDirection,
    status: filter.status.join(','),
  });
  if (filter.brandId) params.set('brandId', filter.brandId);
  return params;
}

export function serializeLineItemsFilter(filter: LineItemsFilter) {
  return new URLSearchParams({
    campaignId: filter.campaignId,
    name: filter.name,
    page: String(filter.page),
    size: String(filter.size),
    sortProperty: filter.sortProperty.join(','),
    sortDirection: filter.sortDirection,
    status: filter.status.join(','),
    deliveryStatus: filter.deliveryStatus.join(','),
  });
}

export function resolveCampaignStatusLabel(status: string | undefined) {
  const labels: Record<CampaignStatus, string> = {
    [CampaignStatus.Active]: dashboardT('campaigns.status.active'),
    [CampaignStatus.Archived]: dashboardT('campaigns.status.archived'),
    [CampaignStatus.Paused]: dashboardT('campaigns.status.paused'),
    [CampaignStatus.Draft]: dashboardT('campaigns.status.draft'),
    [CampaignStatus.Completed]: dashboardT('campaigns.status.completed'),
  };
  return isCampaignStatus(status) ? labels[status] : titleCaseStatus(status);
}

export function resolveCampaignStatusTone(status: string | undefined) {
  if (status === CampaignStatus.Active) return DashboardTableStatusTone.Success;
  if (status === CampaignStatus.Paused) return DashboardTableStatusTone.Warning;
  if (status === CampaignStatus.Completed) return DashboardTableStatusTone.Info;
  return DashboardTableStatusTone.Neutral;
}

export function resolveLineItemDeliveryStatusLabel(status: string | undefined) {
  const labels: Record<LineItemDeliveryStatus, string> = {
    [LineItemDeliveryStatus.Delivering]: dashboardT('lineItems.delivery.delivering'),
    [LineItemDeliveryStatus.GoalMet]: dashboardT('lineItems.delivery.goalMet'),
    [LineItemDeliveryStatus.Completed]: dashboardT('campaigns.status.completed'),
    [LineItemDeliveryStatus.Ready]: dashboardT('lineItems.delivery.ready'),
  };
  return isLineItemDeliveryStatus(status) ? labels[status] : titleCaseStatus(status);
}

export function formatLineItemDate(value: string | undefined) {
  return formatDisplayDate(value, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' });
}

export function formatLineItemCurrency(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) return '-';
  return formatUsd(value);
}

export function resolveLineItemCompletionPercent(lineItem: LineItem) {
  if (lineItem.deliveredImpressionsPct !== undefined) {
    return clampPercent(lineItem.deliveredImpressionsPct);
  }
  if (!lineItem.impressionsGoal) return 0;
  return clampPercent(((lineItem.deliveredImpressions ?? 0) / lineItem.impressionsGoal) * 100);
}

function titleCaseStatus(status: string | undefined) {
  if (!status) return dashboardT('campaigns.status.unknown');
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function isCampaignStatus(status: string | undefined): status is CampaignStatus {
  return Object.values(CampaignStatus).includes(status as CampaignStatus);
}

function isLineItemDeliveryStatus(status: string | undefined): status is LineItemDeliveryStatus {
  return Object.values(LineItemDeliveryStatus).includes(status as LineItemDeliveryStatus);
}

function clampPercent(value: number) {
  return Math.min(100, Math.max(0, value));
}

function toOptional<T>(value: T | null | undefined) {
  return value ?? undefined;
}
